import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef } from "react";

import { BodyM } from "@brexhq/shared/components/Typography/Components";

const ItemText = styled(BodyM)(() => ({
  fontVariationSettings: "'wght' 600 !important",
}));

interface Props {
  text: string;
  isActive?: boolean;
  isFocused?: boolean;
  onActivate: (item: string) => void;
}

const NavMenuButton = styled.button<{ isActive?: boolean }>(
  ({ isActive, theme }) => {
    const borderColor = isActive
      ? theme.colors.content.inverse.secondary
      : "transparent";
    return {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing.LRG.xxsml,
      padding: "4px 8px",
      background: "none",
      border: `1px solid ${borderColor}`,
      borderRadius: 6,
      cursor: "pointer",
      "&:focus": {
        outline: "none",
      },
    };
  },
);

const Arrow = styled.svg<{ isActive?: boolean }>(({ isActive }) => {
  return {
    viewBox: "0 0 11 7",
    fill: "none",
    width: 11,
    height: 7,
    transform: isActive ? undefined : "rotate(180deg)",
  };
});

export const NavMenu = ({ text, isActive, isFocused, onActivate }: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { colors } = useTheme();

  useEffect(() => {
    if (isFocused) {
      buttonRef.current?.focus();
    }
  }, [isFocused]);

  return (
    <NavMenuButton
      ref={buttonRef}
      type="button"
      role="menuitem"
      isActive={isActive}
      aria-haspopup="true"
      aria-expanded={isActive}
      tabIndex={isFocused ? 0 : -1}
      onMouseOver={() => onActivate(text)}
      onFocus={() => onActivate(text)}
    >
      <ItemText as="span">{text}</ItemText>
      <Arrow xmlns="http://www.w3.org/2000/svg" isActive={isActive}>
        <path
          d="M9.61914 5.42871L5.77152 1.53977L1.9239 5.42871"
          stroke={
            isActive
              ? colors.accent.primary.background.default
              : colors.content.primary
          }
          strokeWidth="1.92381"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Arrow>
    </NavMenuButton>
  );
};
